<template>
  <div class="container">
    <div class="title">
      Cut and Paste a Screenshot to PDF
      <button @click="generatePDF">Generate PDF</button>
      <button @click="previewPDF">Preview PDF</button>
      <button class="close-dialog" @click="closeDialog">
        <i class="fas fa-close"></i>
      </button>
    </div>
    <div
      ref="pasteArea"
      class="paste-area"
      contenteditable="true"
      @paste="handlePaste"
    >
      Paste your screenshot here
    </div>

    <!-- Popup for PDF preview -->
    <div v-if="showPopup" class="popup-overlay">
      <div class="popup-content">
        <button @click="closePopup" class="close-btn">Close</button>
        <iframe :src="pdfDataUrl" width="100%" height="500px"></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import jsPDF from 'jspdf';
import eventBus from './eventBus';

export default {
  data() {
    return {
      pastedImage: null,
      showPopup: false,
      pdfDataUrl: '',
    };
  },
  methods: {
    closeDialog() {
      eventBus.emit('ClipPDFCloseDialog');
    },
    handlePaste(event) {
      const clipboardItems = event.clipboardData.items;

      for (let i = 0; i < clipboardItems.length; i++) {
        const item = clipboardItems[i];

        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          const reader = new FileReader();

          reader.onload = (e) => {
            this.pastedImage = e.target.result;
            this.updatePasteArea();
          };

          reader.readAsDataURL(file);
          break;
        }
      }
    },

    updatePasteArea() {
      if (this.pastedImage) {
        const pasteArea = this.$refs.pasteArea;
        pasteArea.innerHTML = `<img src="${this.pastedImage}" class="scaled-image">`;
      }
    },

    generatePDF() {
      if (!this.pastedImage) {
        eventBus.emit('ClipPDFCloseDialog');
        return;
      }

      const img = new Image();
      img.src = this.pastedImage;

      img.onload = () => {
        const imgWidth = img.width;
        const imgHeight = img.height;

        // Define standard page size (A4 in mm)
        const a4WidthInMm = 210;
        const a4HeightInMm = 297;

        // Calculate scale factor
        const widthScale = a4WidthInMm / imgWidth;
        const heightScale = a4HeightInMm / imgHeight;
        const scaleFactor = Math.min(widthScale, heightScale);

        // Scale image dimensions proportionally
        const scaledImgWidthInMm = imgWidth * scaleFactor;
        const scaledImgHeightInMm = imgHeight * scaleFactor;

        const pdf = new jsPDF({
          unit: 'mm',
          format: 'a4',
        });

        // Center the image on the page
        const xOffset = (a4WidthInMm - scaledImgWidthInMm) / 2;
        // const yOffset = (a4HeightInMm - scaledImgHeightInMm) / 2;
        const yOffset = 5;

        pdf.addImage(
          this.pastedImage,
          'PNG',
          xOffset,
          yOffset,
          scaledImgWidthInMm,
          scaledImgHeightInMm,
        );

        const blob = pdf.output('blob');
        this.storePDFInIndexedDB(blob);

        // eventBus.emit('ClipPDFCloseDialog');
      };
    },
    storePDFInIndexedDB(blob) {
      const request = indexedDB.open('pdfDatabase', 1);

      request.onupgradeneeded = function (event) {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('pdfs')) {
          db.createObjectStore('pdfs', { keyPath: 'id' });
        }
      };

      request.onsuccess = function (event) {
        const db = event.target.result;
        const transaction = db.transaction(['pdfs'], 'readwrite');
        const store = transaction.objectStore('pdfs');

        store.put({ id: 'latestPDF', blob: blob });

        transaction.oncomplete = function () {
          console.log('PDF stored successfully in IndexedDB');
        };
      };

      request.onerror = function (event) {
        console.error('Error opening IndexedDB:', event);
      };
    },

    previewPDF() {
      const request = indexedDB.open('pdfDatabase', 1);

      request.onsuccess = (event) => {
        console.log('IndexedDB opened successfully.');
        const db = event.target.result;
        const transaction = db.transaction(['pdfs'], 'readonly');
        const store = transaction.objectStore('pdfs');
        const getRequest = store.get('latestPDF');

        getRequest.onsuccess = (event) => {
          const result = event.target.result;
          if (result && result.blob) {
            console.log('Retrieved blob from IndexedDB:', result.blob);

            // Consider trying object URL first if fileReader isn't working
            this.pdfDataUrl = URL.createObjectURL(result.blob);
            this.showPopup = true;

            // Uncomment the below block if FileReader approach is preferred
            /*
            const fileReader = new FileReader();
            fileReader.onloadend = () => {
              console.log('Successfully converted blob to data URL');
              this.pdfDataUrl =
                'data:application/pdf;base64,' +
                fileReader.result.split(',')[1];
              this.showPopup = true;
            };
            fileReader.readAsDataURL(result.blob);
            */
          } else {
            console.error('No PDF found in IndexedDB.');
          }
        };

        getRequest.onerror = (event) => {
          console.error('Error getting PDF from IndexedDB:', event);
        };
      };

      request.onerror = (event) => {
        console.error('Error opening IndexedDB for preview:', event);
      };
    },

    closePopup() {
      this.showPopup = false;
    },
  },
};
</script>

<style scoped>
.paste-area {
  height: 400px;
  border: 1px dashed #ccc;
  padding: 10px;
  margin-bottom: 10px;
  text-align: center;
  overflow: hidden;
}

.scaled-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: scale-down;
  display: block;
  margin: 0 auto;
}

.container {
  background: #fff;
  margin-left: 10px;
  border: 1pt solid #bbb;
  border-radius: 5px;
  width: 560px;
  height: 450px;
  padding: 20px;
  box-shadow: 11px 9px 7px 2px rgb(0 0 0 / 32%);
  z-index: 1002;
  position: absolute;
  top: 50px;
  left: 50px;
}

.title {
  background: #333;
  color: #eee;
  font-size: 1.2em;
  padding: 10px;
  margin: -21px;
  margin-bottom: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.title button {
  margin-left: 10px;
  background: #a080a8;
  border: 1pt solid #abaaaa;
  padding: 4px;
  border-radius: 4px;
}

.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.popup-content {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  width: 80%;
  max-width: 600px;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  padding: 5px 10px;
}

iframe {
  width: 100%;
  height: 500px;
  border: none;
}

.container {
  background: #fff;
  margin-left: 10px;
  border: 1pt solid #bbb;
  border-radius: 5px;
  width: 83%;
  height: 450px;
  padding: 20px;
  box-shadow: 11px 9px 7px 2px rgb(0 0 0 / 32%);
  z-index: 1002;
  position: absolute;
  top: 12%;
  left: 2%;
}

.close-dialog {
  position: absolute;
  right: 10px;
  top: 7px;
  background: #333 !important;
  color: white !important;
  width: 22px;
  font-weight: bold;
  height: 23px;
  font-size: 0.9em;
  padding: 0 !important;
}
</style>
